/**
 * Extracts the rep_id from a user ID string
 * @param {string} userId - The full user ID (e.g., "comp_6_rep_10003")
 * @returns {string|null} - The rep_id portion (e.g., "10003") or null if not found
 */
export const extractRepId = userId => {
  if (!userId || typeof userId !== "string") {
    return null;
  }

  const repIndex = userId.lastIndexOf("rep_");
  if (repIndex === -1) {
    return null;
  }

  return userId.substring(repIndex + 4); // +4 to skip "rep_"
};

/**
 * Checks if a message belongs to the current user by comparing rep_ids
 * @param {object} message - The message object with sender information
 * @param {object} currentUser - The current user object
 * @returns {boolean} - True if the message belongs to the current user
 */
export const isMessageFromCurrentUser = (message, currentUser) => {
  if (!message?.sender?.userId || !currentUser?.userId) {
    return false;
  }

  if (message.sender.userId === currentUser.userId) {
    return true;
  }

  const messageRepId = extractRepId(message.sender.userId);
  const currentUserRepId = extractRepId(currentUser.userId);

  if (!messageRepId || !currentUserRepId) {
    // Fallback to full ID comparison if rep_id extraction fails
    return message.sender.userId === currentUser.userId;
  }

  return messageRepId === currentUserRepId;
};

/**
 * Maps a user role constant to the short label shown in the navigation chrome
 * (sidebar header badge and the sidebar footer user card).
 * @param {string} role - The user's role (e.g. "COMPANY_ADMIN")
 * @returns {string} - Display label (e.g. "Admin"), or "" when the role is unknown
 */
export const getRoleLabel = role => {
  if (!role || typeof role !== "string") {
    return "";
  }

  const ROLE_LABELS = {
    COMPANY_ADMIN: "Admin",
    REPPI_ADMIN: "Reppi Admin",
    MANAGER: "Manager",
    REP: "Rep",
  };

  return ROLE_LABELS[role] || role.replace(/_/g, " ");
};
